import themes from "daisyui/src/theming/themes";

interface PlanFeature {
  name: string;
}

interface StripePlan {
  priceId: string;
  name: string;
  description: string;
  price: number;
  priceFrequencyDescription: string;
  priceFrequency: string;
  priceAnchor?: number;
  features: PlanFeature[];
  isFeatured?: boolean;
}

interface StripeConfig {
  plans: StripePlan[];
}

interface CrispConfig {
  id: string;
  onlyShowOnRoutes?: string[];
}

interface AWSConfig {
  bucket: string;
  bucketUrl: string;
  cdn: string;
}

interface MailgunConfig {
  subdomain?: string;
  fromNoReply: string;
  fromAdmin: string;
  supportEmail: string;
  forwardRepliesTo: string;
}

interface ColorsConfig {
  theme: string;
  main: string;
}

interface AuthConfig {
  loginUrl: string;
  callbackUrl: string;
}

interface Config {
  appName: string;
  appDescription: string;
  domainName: string;
  crisp: CrispConfig;
  stripe: StripeConfig;
  aws: AWSConfig;
  mailgun: MailgunConfig;
  colors: ColorsConfig;
  auth: AuthConfig;
}

const config: Config = {
  // REQUIRED
  appName: "BudgetBuddies",
  // REQUIRED: a short description of your app for SEO tags (can be overwritten)
  appDescription:
    "Social finance application that makes discussing finances less taboo by sharing your finance goals, budgets, and accomplishments with friends and strangers alike!",
  // REQUIRED (no https://, not trialing slash at the end, just the naked domain)
  domainName: "budgetbuddies.app",
  crisp: {
    // Crisp website ID. IF YOU DON'T USE CRISP: just remove this => Then add a support email in this config file (mailgun.supportEmail) otherwise customer support won't work.
    id: "",
    // Hide Crisp by default, except on route "/". Crisp is toggled with <ButtonSupport/>. If you want to show Crisp on every routes, just remove this below
    onlyShowOnRoutes: ["/"],
  },
  stripe: {
    // Create multiple plans in your Stripe dashboard, then add them here. You can add as many plans as you want, just make sure to add the priceId
    plans: [
      {
        // REQUIRED — we use this to find the plan in the webhook (for instance if you want to update the user's credits based on the plan)
        priceId:
          process.env.NODE_ENV === "development"
            ? "price_1RmaRKFfyK7NR1zj67BSUqWW"
            : "price_1RmaRKFfyK7NR1zj67BSUqWW",
        //  REQUIRED - Name of the plan, displayed on the pricing page
        name: "Free",
        // A friendly description of the plan, displayed on the pricing page. Tip: explain why this plan and not others
        description:
          "Perfect for beginners or those who want to try out the app!",
        // The price you want to display, the one user will be charged on Stripe.
        price: 0,
        priceFrequencyDescription: "Always free, no credit card required.",
        priceFrequency: "USD/Month",
        // If you have an anchor price (i.e. $29) that you want to display crossed out, put it here. Otherwise, leave it empty
        // priceAnchor: 99,
        features: [
          {
            name: "Basic budgeting tools",
          },
          { name: "Manual transactions" },
          { name: "Limited community access" },
          { name: "Limited goal tracking" },
        ],
      },
      {
        // This plan will look different on the pricing page, it will be highlighted. You can only have one plan with isFeatured: true
        isFeatured: false,
        priceId:
          process.env.NODE_ENV === "development"
            ? "price_1RmaRKFfyK7NR1zjCsVNJ69c"
            : "price_1RmaRKFfyK7NR1zjCsVNJ69c",
        name: "Pro",
        description:
          "You need more control over your finances and to have fun!",
        price: 4.99,
        priceAnchor: 6.99,
        priceFrequencyDescription: "Pay monthly, cancel anytime.",
        priceFrequency: "USD/Month",
        features: [
          {
            name: "Auto import transactions",
          },
          { name: "Full community access" },
          { name: "Full goal tracking" },
          { name: "Compete in friendly challenges/competitions" },
          { name: "Customizable dashboards" },
          { name: "24/7 Support" },
        ],
      },
      {
        // This plan will look different on the pricing page, it will be highlighted. You can only have one plan with isFeatured: true
        isFeatured: true,
        priceId:
          process.env.NODE_ENV === "development"
            ? "price_1RmaRKFfyK7NR1zjsmjFBM3y"
            : "price_1RmaRKFfyK7NR1zjsmjFBM3y",
        name: "Pro Yearly",
        description:
          "What Warren Buffet would do if he had to start from scratch!!",
        price: 75,
        priceAnchor: 119.76,
        priceFrequencyDescription: "Pay once, access forever.",
        priceFrequency: "USD/Year",
        features: [
          {
            name: "Auto import transactions",
          },
          { name: "Full community access" },
          { name: "Full goal tracking" },
          { name: "Compete in friendly challenges/competitions" },
          { name: "Customizable dashboards" },
          { name: "24/7 Support" },
        ],
      },
    ],
  },
  aws: {
    // If you use AWS S3/Cloudfront, put values in here
    bucket: "bucket-name",
    bucketUrl: `https://bucket-name.s3.amazonaws.com/`,
    cdn: "https://cdn-id.cloudfront.net/",
  },
  mailgun: {
    // subdomain to use when sending emails, if you don't have a subdomain, just remove it. Highly recommended to have one (i.e. mg.yourdomain.com or mail.yourdomain.com)
    // subdomain: "mg",
    // REQUIRED — Email 'From' field to be used when sending magic login links
    fromNoReply: `BudgetBuddies <noreply@budgetbuddies.app>`,
    // REQUIRED — Email 'From' field to be used when sending other emails, like abandoned carts, updates etc..
    fromAdmin: `Stephon at BudgetBuddies <owner@budgetbuddies.app>`,
    // Email shown to customer if need support. Leave empty if not needed => if empty, set up Crisp above, otherwise you won't be able to offer customer support."
    supportEmail: "support@budgetbuddies.app",
    // When someone replies to supportEmail sent by the app, forward it to the email below (otherwise it's lost). If you set supportEmail to empty, this will be ignored.
    forwardRepliesTo: "support@budgetbuddies.app",
  },
  colors: {
    // REQUIRED — The DaisyUI theme to use (added to the main layout.js). Leave blank for default (light & dark mode). If you any other theme than light/dark, you need to add it in config.tailwind.js in daisyui.themes.
    theme: "dark",
    // REQUIRED — This color will be reflected on the whole app outside of the document (loading bar, Chrome tabs, etc..). By default it takes the primary color from your DaisyUI theme (make sure to update your the theme name after "data-theme=")
    // OR you can just do this to use a custom color: main: "#f37055". HEX only.
    // main: themes["#06FB08"],
    main: themes["light"]["primary"] as string,
  },
  auth: {
    // REQUIRED — the path to log in users. It's use to protect private routes (like /dashboard). It's used in apiClient (/libs/api.ts) upon 401 errors from our API
    loginUrl: "/signin",
    // REQUIRED — the path you want to redirect users after successfull login (i.e. /dashboard, /private). This is normally a private page for users to manage their accounts. It's used in apiClient (/libs/api.ts) upon 401 errors from our API & in ButtonSignin.tsx
    callbackUrl: "/dashboard",
  },
};

export default config;